import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Logo } from '@/teile/Logo'
import { ApiError } from '../api/client.js'
import { anmelden, loeseUebergabeEin, zweiFaktorBestaetigen } from '../api/session.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Zwei Wege in dieselbe Anwendung.
 *
 * **Aus der App**: Die App öffnet `/anmelden#token=…`. Das Token steht im
 * Fragment — es wird nicht an den Server gesendet, landet in keinem
 * Zugriffsprotokoll und nicht im Referrer. Direkt nach dem Lesen verschwindet
 * es aus der Adresszeile, damit es nicht im Verlauf des Browsers stehen
 * bleibt. Verbraucht ist es dann ohnehin.
 *
 * **Ohne App**: Steht kein Token in der Adresse, erscheint die Anmeldung mit
 * E-Mail und Passwort (ADR-0004 Nachtrag 5). Sie führt in dieselbe Oberfläche
 * mit denselben Rechten; sie ist nur der vorgelagerte Weg für ein Gerät, auf
 * dem es die App nicht gibt — ein Tablet etwa.
 */

type Zustand =
  | { status: 'uebergabe' }
  | { status: 'formular' }
  /** Passwort stimmt, jetzt fehlt der Code aus der Authenticator-App. */
  | { status: 'zweiter_faktor' }
  | { status: 'fehler'; meldung: string }

export function Anmelden() {
  const [zustand, setZustand] = useState<Zustand>({ status: 'uebergabe' })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const navigate = useNavigate()
  const { neuLaden } = useSitzung()
  // React führt Effekte im StrictMode zweimal aus. Ohne diese Sperre liefe der
  // zweite Lauf gegen ein bereits verbrauchtes Token und zeigte einen Fehler,
  // obwohl alles geklappt hat.
  const bereitsVersucht = useRef(false)

  useEffect(() => {
    if (bereitsVersucht.current) return
    bereitsVersucht.current = true

    const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')
    if (!token) {
      // Kein Token: Das ist kein Fehler mehr, sondern der zweite Weg.
      setZustand({ status: 'formular' })
      return
    }
    window.history.replaceState(null, '', window.location.pathname)

    void (async () => {
      try {
        await loeseUebergabeEin(token)
        await neuLaden()
        await navigate('/', { replace: true })
      } catch (error) {
        setZustand({
          status: 'fehler',
          meldung:
            error instanceof ApiError
              ? error.message
              : 'Der Zugang konnte nicht hergestellt werden.',
        })
      }
    })()
  }, [navigate, neuLaden])

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const formular = new FormData(ereignis.currentTarget)

    setLaeuft(true)
    setMeldung(null)
    try {
      const antwort = await anmelden(
        String(formular.get('email') ?? ''),
        String(formular.get('passwort') ?? ''),
      )
      // Ist ein zweiter Faktor eingerichtet, gibt es noch keine Sitzung —
      // Better Auth verlangt erst den Code.
      if (antwort.zweiterFaktor) {
        setZustand({ status: 'zweiter_faktor' })
        return
      }
      await neuLaden()
      await navigate('/', { replace: true })
    } catch (fehler) {
      // Eine Meldung für alle Fälle: unbekannte Adresse, falsches Passwort,
      // gesperrtes Konto, Konto ohne Passwort. Wer hier unterscheidet, verrät,
      // welche Adressen es gibt (SECURITY.md §2).
      setMeldung(
        fehler instanceof ApiError && fehler.status === 0
          ? fehler.message
          : 'Anmeldung nicht möglich. Bitte E-Mail und Passwort prüfen.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function codeSenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const code = String(new FormData(ereignis.currentTarget).get('code') ?? '').trim()
    setLaeuft(true)
    setMeldung(null)
    try {
      await zweiFaktorBestaetigen(code)
      await neuLaden()
      await navigate('/', { replace: true })
    } catch {
      setMeldung('Der Code stimmt nicht. Bitte den aktuellen aus Ihrer App eingeben.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center p-8">
      {/* Das Logo haengt ueber dem Block und zaehlt fuer die Mitte nicht mit:
          Das Formular bleibt mittig, das Logo steht darueber. */}
      <div className="relative flex flex-col gap-7">
        <Logo className="absolute bottom-full left-1/2 mb-8 w-[23.5rem] max-w-none -translate-x-1/2 [@media(min-height:880px)]:w-[30.5rem]" />

        {zustand.status === 'fehler' ? (
          <Hinweis art="fehler" titel="Zugang nicht möglich">
            <p>{zustand.meldung}</p>
            <p className="mt-2">
              Der Zugang gilt nur eine Minute und nur einmal. Öffnen Sie die Einstellungen in der
              App erneut.
            </p>
          </Hinweis>
        ) : zustand.status === 'zweiter_faktor' ? (
          <form onSubmit={codeSenden} className="flex flex-col gap-4">
            <div>
              <h1 className="font-display text-xl font-bold text-primary">Zweiter Faktor</h1>
            </div>
            <Input
              isRequired
              name="code"
              label="Code"
              autoComplete="one-time-code"
              inputMode="numeric"
              size="md"
            />
            {meldung ? (
              <p
                role="status"
                className="rounded-lg bg-error-primary p-3 text-sm text-error-primary"
              >
                {meldung}
              </p>
            ) : null}
            <Button type="submit" size="lg" isLoading={laeuft}>
              Weiter
            </Button>
          </form>
        ) : zustand.status === 'uebergabe' ? (
          <p role="status" className="text-sm text-tertiary">
            Zugang wird hergestellt …
          </p>
        ) : (
          <>
            <form onSubmit={absenden} className="flex flex-col gap-4">
              <Input
                isRequired
                name="email"
                type="email"
                label="E-Mail"
                autoComplete="username"
                size="md"
              />
              <Input
                isRequired
                name="passwort"
                type="password"
                label="Passwort"
                autoComplete="current-password"
                size="md"
              />

              {meldung ? (
                <p
                  role="status"
                  className="rounded-lg bg-error-primary p-3 text-sm text-error-primary"
                >
                  {meldung}
                </p>
              ) : null}

              <Button type="submit" size="lg" isLoading={laeuft}>
                Anmelden
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  )
}
